import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { api } from '@/utils/api';
import Landing from '@/pages/Landing';
import Properties from '@/pages/Properties';
import PropertyDetail from '@/pages/PropertyDetail';
import AdminDashboard from '@/pages/AdminDashboard';
import AuthEnhanced from '@/pages/AuthEnhanced';
import BrokerAuth from '@/pages/BrokerAuth';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Navbar from '@/components/Navbar';
import Blog from '@/pages/Blog';
import BlogDetail from '@/pages/BlogDetail';
// Broker Dashboard Pages
import BrokerDashboard from '@/pages/BrokerDashboard';
import BrokerDemo from '@/pages/BrokerDemo';
import BrokerDashboardTour from '@/pages/BrokerDashboardTour';
import BrokerMarketplace from '@/pages/BrokerMarketplace';
import BrokerLedger from '@/pages/BrokerLedger';
import BrokerInvestors from '@/pages/BrokerInvestors';
import BrokerWallet from '@/pages/BrokerWallet';
import BrokerLeaderboard from '@/pages/BrokerLeaderboard';
import BrokerReferrals from '@/pages/BrokerReferrals';
import BrokerEDMGenerator from '@/pages/BrokerEDMGenerator';
import BrokerSettings from '@/pages/BrokerSettings';
import BrokerTeamManagement from '@/pages/BrokerTeamManagement';
import SecondaryMarketplace from '@/pages/SecondaryMarketplace';
import InvestorDashboardUnified from '@/pages/InvestorDashboardUnified';
import EstateDetailNew from '@/pages/EstateDetailNew';
import InvestmentFlow from '@/pages/InvestmentFlow';
import InvestmentDetail from '@/pages/InvestmentDetail';
import EmbeddedSigningPage from '@/pages/EmbeddedSigningPage';
import GovernanceVoting from '@/pages/GovernanceVoting';
import CapitalCallFlowChart from '@/pages/CapitalCallFlowChart';
import BrokerPipeline from '@/pages/broker/BrokerPipeline';
import InvestmentLinkLanding from '@/pages/public/InvestmentLinkLanding';
// Re-export api for backward compatibility
export { api } from '@/utils/api';

function ProtectedRoute({ children, allowedRoles }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (!token) {
    return <Navigate to="/auth" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'broker') return <Navigate to="/broker" replace />;
    return <Navigate to="/investor" replace />;
  }
  
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (!token || !savedUser) {
        setAuthLoading(false);
        return;
      }
      
      try {
        // Validate token by calling /auth/me
        const response = await api.get('/auth/me');
        if (response.data) {
          setUser(JSON.parse(savedUser));
        }
      } catch (error) {
        // Token is invalid or expired, clear localStorage
        console.warn('Session expired, clearing auth data');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setAuthLoading(false);
      }
    };
    
    validateSession();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Navbar user={user} setUser={setUser} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/blog" element={<Blog user={user} setUser={setUser} />} />
          <Route path="/blog/:slug" element={<BlogDetail user={user} setUser={setUser} />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/properties/:id" element={<PropertyDetail user={user} />} />
          <Route path="/auth" element={<AuthEnhanced setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Admin Dashboard */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Broker Dashboard Routes */}
          <Route path="/broker/auth" element={<BrokerAuth setUser={setUser} />} />
          <Route path="/broker" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerDashboard /></ProtectedRoute>} />
          <Route path="/broker/demo" element={<BrokerDemo />} />
          <Route path="/broker/demo/dashboard-tour" element={<BrokerDashboardTour />} />
          <Route path="/broker/marketplace" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerMarketplace /></ProtectedRoute>} />
          <Route path="/broker/investors" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerInvestors /></ProtectedRoute>} />
          <Route path="/broker/ledger" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerLedger /></ProtectedRoute>} />
          <Route path="/broker/wallet" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerWallet /></ProtectedRoute>} />
          <Route path="/broker/leaderboard" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerLeaderboard /></ProtectedRoute>} />
          <Route path="/broker/referrals" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerReferrals /></ProtectedRoute>} />
          <Route path="/broker/edm" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerEDMGenerator /></ProtectedRoute>} />
          <Route path="/broker/settings" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerSettings /></ProtectedRoute>} />
          <Route path="/broker/team" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerTeamManagement /></ProtectedRoute>} />
          <Route path="/broker/secondary" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><SecondaryMarketplace /></ProtectedRoute>} />
          <Route path="/broker/pipeline" element={<ProtectedRoute allowedRoles={['broker', 'admin']}><BrokerPipeline /></ProtectedRoute>} />
          
          {/* Public Assignment Link (no auth) */}
          <Route path="/i/:token" element={<InvestmentLinkLanding />} />
          
          {/* Investor Dashboard - Unified Single Page */}
          <Route 
            path="/investor" 
            element={
              <ProtectedRoute allowedRoles={['investor', 'admin']}>
                <InvestorDashboardUnified user={user} setUser={setUser} />
              </ProtectedRoute>
            } 
          />
          
          {/* Legacy route redirects */}
          <Route path="/investor/estates" element={<Navigate to="/investor" replace />} />
          <Route path="/investor/estates/*" element={<Navigate to="/investor" replace />} />
          <Route path="/dashboard" element={<Navigate to="/investor" replace />} />
          
          {/* Estate Detail */}
          <Route 
            path="/estate/:id" 
            element={
              <ProtectedRoute allowedRoles={['investor', 'admin']}>
                <EstateDetailNew user={user} setUser={setUser} />
              </ProtectedRoute>
            } 
          />
          
          {/* Governance Voting */}
          <Route 
            path="/governance" 
            element={
              <ProtectedRoute>
                <GovernanceVoting />
              </ProtectedRoute>
            } 
          />
          
          {/* Capital Call Flow Chart (Admin/Planning) */}
          <Route path="/capital-call-flow" element={<CapitalCallFlowChart />} />
          
          {/* Investment Flow */}
          <Route 
            path="/invest/:propertyId" 
            element={
              <ProtectedRoute allowedRoles={['investor', 'admin']}>
                <InvestmentFlow />
              </ProtectedRoute>
            } 
          />
          
          {/* Document Signing */}
          <Route 
            path="/investor/sign/:investmentId" 
            element={
              <ProtectedRoute allowedRoles={['investor', 'admin']}>
                <EmbeddedSigningPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Investment Detail with Journey Tracker */}
          <Route 
            path="/investor/investment/:investmentId" 
            element={
              <ProtectedRoute allowedRoles={['investor', 'admin']}>
                <InvestmentDetail user={user} />
              </ProtectedRoute>
            } 
          />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;